import { Fragment, type ReactNode } from 'react';
import { darkTheme, FONT } from '../../theme/tokens';

const t = darkTheme;

// Calm-blue link-button style for a banner action (Back / Clear). Module-local: the
// shell owns action rendering, so no consumer needs the raw style.
const linkButton = {
  background: 'none',
  border: 'none',
  color: t.blueText,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  padding: 0,
  fontFamily: FONT,
  whiteSpace: 'nowrap',
} as const;

// Caption shown when an agent navigation carried no narration text (off-contract —
// narration is meant to be a populated "why I brought you here"). The banner + its
// Clear still render so an agent-driven view is never presented without a visible
// reason and a one-click dismiss (same discipline as the table caption).
const FALLBACK_CAPTION = 'EKOM opened this view';

interface ProvenanceAction {
  label: string;
  onClick: () => void;
}

interface ProvenanceBannerShellProps {
  /** Leading glyph — the shared "⤳ arrived here from chat" mark. */
  glyph?: string;
  /** The banner's message (the flexible middle). */
  children: ReactNode;
  /** Right-aligned actions, rendered as link buttons with `·` separators. */
  actions: ProvenanceAction[];
}

/**
 * The shared provenance-banner chrome (SC-2663): the calm-blue bar that says "you
 * arrived at this view via the agent, here's why, here's the way out." Both the
 * frame-level `ProvenanceBanner` (any agent nav) and the table's finding-scoped
 * `FindingProvenanceBanner` (SC-2637) render into it, so the two read as one visual
 * language and only the message + actions differ.
 */
export function ProvenanceBannerShell({
  glyph = '⤳',
  children,
  actions,
}: ProvenanceBannerShellProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 14px',
        marginBottom: 16,
        background: t.blueBg,
        border: `1px solid ${t.blue}`,
        borderRadius: 8,
        color: t.blueText,
        fontSize: 13,
        fontFamily: FONT,
      }}
    >
      <span aria-hidden>{glyph}</span>
      <span style={{ flex: 1 }}>{children}</span>
      {actions.map((action, i) => (
        <Fragment key={action.label}>
          {i > 0 && (
            <span aria-hidden style={{ opacity: 0.4 }}>
              ·
            </span>
          )}
          <button type="button" onClick={action.onClick} style={linkButton}>
            {action.label}
          </button>
        </Fragment>
      ))}
    </div>
  );
}

interface ProvenanceBannerProps {
  /** The agent's "why I brought you here" caption. `''` is allowed — the fallback
   *  caption renders in its place; the banner shows regardless. */
  narration: string;
  /** Dismiss the provenance note and stay on the view (→ `clearProvenance`). */
  onClear: () => void;
}

/**
 * The frame-level agent-navigation banner (Canvas P4, SC-2663). Rendered once above
 * the canvas by `WorkspaceShell` whenever `workspaceStore.provenance.source ===
 * 'agent'` — i.e. an agent directive (the real `open_view` tool, or its DEV `/open`
 * stub) navigated the workspace to a NON-entities view. Its mere presence is the
 * "you didn't get lost — EKOM opened this" signal; Clear drops the note without
 * leaving the view. The entities view has no frame banner — its table caption is its
 * own provenance surface (see `workspaceStore.applyCanvasDirective`). Generalizes
 * SC-2637's `FindingProvenanceBanner`, which shares the same shell.
 */
export function ProvenanceBanner({ narration, onClear }: ProvenanceBannerProps) {
  return (
    <ProvenanceBannerShell actions={[{ label: 'Clear', onClick: onClear }]}>
      {narration.trim() || FALLBACK_CAPTION}
    </ProvenanceBannerShell>
  );
}
